// WPS-012 · Template Editor admin
// CRUD templates with live CardCanvas preview · category-aware · tier-aware
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Copy, Save, X } from 'lucide-react';
import CardCanvas from '../CardCanvas';
import { templatesApi } from '../../services/api';

const fetchCategories = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  return fetch(`${base}/categories`).then(r => r.json()).catch(() => []);
};

const fetchAdminTemplates = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  const token = localStorage.getItem('admin_token');
  return fetch(`${base}/templates`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }).then(r => r.json()).catch(() => []);
};

const EMPTY = {
  name: '', categorySlug: '', style: 'bold', tier: 'signature',
  colors: ['#0a0a0a', '#c9a961', '#ffffff'],
  defaultSlogan: '', icon: '', isActive: true, displayOrder: 1000,
  frontDesign: { layout: 'side-bar-left', qrPosition: 'back' },
  backDesign: { enabled: true, layout: 'minimal-text+qr', showQR: true },
};

const TemplateEditor = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create' });
  const [draft, setDraft] = useState(EMPTY);
  const [filter, setFilter] = useState({ category: 'all', style: 'all' });

  const reload = async () => {
    setLoading(true);
    const [cats, tpls] = await Promise.all([fetchCategories(), fetchAdminTemplates()]);
    setCategories(cats || []);
    setTemplates(tpls || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const openCreate = () => { setDraft(EMPTY); setDialog({ open: true, mode: 'create' }); };
  const openEdit = (t) => { setDraft({ ...EMPTY, ...t }); setDialog({ open: true, mode: 'edit' }); };
  const openDuplicate = (t) => {
    const copy = { ...t };
    delete copy.id;
    copy.name = `${t.name} (Copy)`;
    setDraft({ ...EMPTY, ...copy });
    setDialog({ open: true, mode: 'create' });
  };

  const save = async () => {
    const payload = { ...draft };
    delete payload._id;
    let result;
    if (dialog.mode === 'edit' && draft.id) {
      result = await templatesApi.update(draft.id, payload);
    } else {
      delete payload.id;
      result = await templatesApi.create(payload);
    }
    if (result?.success) {
      setDialog({ open: false, mode: 'create' });
      await reload();
    } else {
      alert('Save failed');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    const r = await templatesApi.remove(t.id);
    if (r?.success) await reload();
  };

  const filtered = templates.filter(t => {
    if (filter.category !== 'all' && t.categorySlug !== filter.category) return false;
    if (filter.style !== 'all' && t.style !== filter.style) return false;
    return true;
  });

  const updateDraftColor = (idx, value) => {
    const colors = [...(draft.colors || ['#000', '#fff', '#ccc'])];
    colors[idx] = value;
    setDraft({ ...draft, colors });
  };

  const sampleData = {
    companyName: draft.name?.split('·')[0]?.trim() || 'Acme Services',
    personalName: 'Carlos Herrera',
    jobTitle: 'Owner',
    email: 'carlos@acme.com',
    phone: '+1 214 949 7999',
    website: 'acme.com',
    slogan: draft.defaultSlogan || '',
    colors: draft.colors,
    orientation: 'horizontal',
    qrPosition: draft.frontDesign?.qrPosition || 'back',
    backDesign: draft.backDesign,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Template Editor</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Edit · duplicate · create category-aware templates · {templates.length} total
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All categories</option>
              {categories.map(c => (
                <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
              ))}
            </select>
            <select
              value={filter.style}
              onChange={(e) => setFilter({ ...filter, style: e.target.value })}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All styles</option>
              <option value="bold">Bold</option>
              <option value="minimal">Minimal</option>
            </select>
            <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800">
              <Plus className="h-4 w-4 mr-2" />New Template
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-12 text-gray-500 font-mono text-sm">Loading…</div>}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">No templates match filters.</div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(t => (
              <div key={t.id} className="border border-gray-200 p-4 hover:border-blue-400 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{t.name}</div>
                    <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mt-1">
                      {t.categorySlug || 'universal'} · {t.style || '—'}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEdit(t)} title="Edit">
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDuplicate(t)} title="Duplicate">
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(t)} title="Delete" className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {(t.colors || []).slice(0, 3).map((c, i) => (
                    <div key={i} style={{ width: 16, height: 16, backgroundColor: c, border: '1px solid #e2e8f0' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', background: '#f1f5f9', padding: '8px 0' }}>
                  <CardCanvas
                    cardData={{
                      companyName: 'Sample Co.',
                      personalName: 'Name Surname',
                      email: 'hello@sample.com',
                      phone: '555-0100',
                      website: 'sample.com',
                      slogan: t.defaultSlogan || '',
                      colors: t.colors,
                      orientation: 'horizontal',
                    }}
                    tier="signature"
                    watermark={false}
                    scale={0.7}
                    side="front"
                    showStockBadge={false}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Editor dialog */}
        <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {dialog.mode === 'edit' ? 'Edit Template' : 'New Template'}
              </DialogTitle>
            </DialogHeader>

            <div className="grid md:grid-cols-2 gap-6 py-4">
              {/* Form */}
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. Plumbing · Bold"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={draft.categorySlug || ''}
                      onValueChange={(v) => setDraft({ ...draft, categorySlug: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.slug} value={c.slug}>{c.icon} {c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Style</Label>
                    <Select value={draft.style} onValueChange={(v) => setDraft({ ...draft, style: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bold">Bold</SelectItem>
                        <SelectItem value="minimal">Minimal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Tier (default for cards using this template)</Label>
                  <Select value={draft.tier} onValueChange={(v) => setDraft({ ...draft, tier: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="signature">Signature</SelectItem>
                      <SelectItem value="atelier">Atelier</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[0, 1, 2].map(i => (
                    <div key={i}>
                      <Label className="text-xs">{['Primary', 'Accent', 'Neutral'][i]}</Label>
                      <div className="flex gap-1 mt-1">
                        <input type="color" value={draft.colors?.[i] || '#000000'}
                          onChange={(e) => updateDraftColor(i, e.target.value)}
                          className="w-8 h-8 border border-gray-300" />
                        <Input
                          value={draft.colors?.[i] || ''}
                          onChange={(e) => updateDraftColor(i, e.target.value)}
                          className="font-mono text-xs flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <Label>Default Slogan</Label>
                  <Input
                    value={draft.defaultSlogan || ''}
                    onChange={(e) => setDraft({ ...draft, defaultSlogan: e.target.value })}
                    placeholder="Reliable repairs · 24/7 service"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>QR Position</Label>
                    <Select
                      value={draft.frontDesign?.qrPosition || 'back'}
                      onValueChange={(v) => setDraft({ ...draft, frontDesign: { ...(draft.frontDesign || {}), qrPosition: v } })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="front">Front</SelectItem>
                        <SelectItem value="back">Back</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch checked={draft.isActive} onCheckedChange={(c) => setDraft({ ...draft, isActive: c })} />
                    <Label>Active</Label>
                  </div>
                </div>
              </div>

              {/* Live preview */}
              <div className="bg-gray-50 p-4 flex flex-col items-center">
                <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Live Preview</div>
                <CardCanvas
                  cardData={sampleData}
                  tier={draft.tier || 'signature'}
                  watermark={false}
                  scale={0.95}
                  side="front"
                  showStockBadge={true}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog({ open: false, mode: 'create' })}>
                <X className="h-4 w-4 mr-2" />Cancel
              </Button>
              <Button onClick={save} className="bg-blue-700 hover:bg-blue-800">
                <Save className="h-4 w-4 mr-2" />Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TemplateEditor;
